import Link from 'next/link';
import { getDisplayCategoryForAsset } from '@frameio/graphql-client';
import type { AudioAsset, VideoAsset } from '@frameio/graphql-client/generated';
import { date as formatDate, plural, time as formatTime } from '@frameio/i18n';
import { VersionCount } from '@frameio/shared/finder/components';
import { getThumbMedia } from '@frameio/shared/finder/utils';
import { getCopyByFileType, timeToStandard } from '@frameio/utils';
import { formatBytes } from '@frameio/utils/formatters';
import {
  CommentCountLabel,
  Inline,
  MediaThumbnail,
  Stack,
  Text,
  theme,
  TimecodeLabel,
  VisuallyHidden,
} from '@frameio/vapor';
import {
  IconClock,
  IconCloud,
  IconFiletypeFile,
  IconFolderClosed,
} from '@frameio/vapor-icons';
import type { QuickSearchAssetFragment } from '../../gql/generated/QuickSearchAsset';
import { useQuickSearchAssetFragment } from '../../gql/generated/QuickSearchAsset';
import { useNavigateToResult } from '../../hooks';
import { isFolder } from '../../utils';
import { MetadataRow } from './components/MetadataRow';
import {
  CREATOR_NAME_MAX_WIDTH,
  DetailContainer,
  DetailPane,
  FileNameAnchor,
  MetaContainer,
  ProjectOrFolderThumbContainer,
  TextWrapper,
  ThumbContainer,
  ThumbnailLinkContainer,
} from './DetailPane';
import TEXT from './generated/TEXT/QuickSearch_AssetDetailPane';

const THUMB_DEFAULT_WIDTH = 236;
const THUMB_DEFAULT_HEIGHT = 133;

TEXT.DEFINE({
  /** Label for project/folder type */
  projectOrFolderType: 'Type',
  /** Label for file type */
  fileType: 'Filetype',
  /** Label for item count */
  itemCount: 'Items',
  /** Label for size */
  size: 'Size',
  /** Accessible label for the comment count indicator (e.g. "5 comments"). */
  commentCountLabel: ({ count }: { count: number }): string =>
    plural(count, `${count} comments`, '1 comment'),
  /**
   * Inserted date/time in readable format
   * Example (en-US): "Jan 15, 2026 at 2:30 PM"
   */
  insertedDateTime: (insertedTime: Date): string =>
    `${formatDate(insertedTime, 'medium')} at ${formatTime(
      insertedTime,
      'short'
    )}`,
  /**
   * Inserted date/time in full a11y format
   * Example (en-US): "January 15, 2026 at 2:30 PM"
   */
  insertedDateTimeA11y: (insertedTime: Date): string =>
    `${formatDate(insertedTime, 'long')} at ${formatTime(
      insertedTime,
      'short'
    )}`,
});

type AssetDetailPaneProps = {
  resultId: string;
};

export const AssetDetailPane = ({ resultId }: AssetDetailPaneProps) => {
  const { data: assetResultData, complete } = useQuickSearchAssetFragment({
    from: { __typename: 'Asset', id: resultId },
  });

  const { href, navigate } = useNavigateToResult(
    resultId,
    assetResultData.__typename ?? ''
  );

  const asset = assetResultData;
  const assetEntity =
    asset.__typename === 'VersionStackAsset' ? asset?.versions?.[0] : asset;

  const commentCount =
    (assetEntity &&
      'commentCount' in assetEntity &&
      assetEntity?.commentCount) ??
    0;
  const filesize =
    asset && 'filesize' in asset
      ? asset?.filesize
      : assetEntity && 'media' in assetEntity && assetEntity?.media?.filesize;
  const itemCount = (asset && 'itemCount' in asset && asset?.itemCount) || 0;
  const metadata =
    assetEntity &&
    'media' in assetEntity &&
    assetEntity.media &&
    'metadata' in assetEntity.media &&
    assetEntity?.media?.metadata;
  const originalWidth = metadata && metadata.originalWidth;
  const originalHeight = metadata && metadata.originalHeight;
  const assetWidth =
    originalWidth && originalWidth < THUMB_DEFAULT_WIDTH
      ? originalWidth
      : THUMB_DEFAULT_WIDTH;
  const assetHeight =
    originalHeight && originalHeight < THUMB_DEFAULT_HEIGHT
      ? originalHeight
      : THUMB_DEFAULT_HEIGHT;

  const formattedFilesize = formatBytes(filesize || 0);
  const assetDuration = timeToStandard(
    ((assetEntity as VideoAsset) || (assetEntity as AudioAsset))?.media
      ?.duration || 0
  );
  const insertedAtDate = assetEntity?.insertedAt
    ? new Date(assetEntity.insertedAt)
    : null;
  const { copy } = getCopyByFileType(
    (assetEntity as any)?.assetType || assetEntity?.__typename,
    (assetEntity as any)?.media?.mimeType
  );

  const isItemFolder = isFolder(assetEntity?.__typename ?? '');

  const handleClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    if (!e.ctrlKey && !e.metaKey) {
      navigate();
      e.preventDefault();
    }
  };

  if (!assetEntity?.id) return null;

  const displayCategory = getDisplayCategoryForAsset(assetEntity);

  return (
    <DetailPane onDoubleClick={navigate}>
      <ThumbnailLinkContainer>
        <Link href={href}>
          <span data-testid="detailpane-thumb-link" onClick={handleClick}>
            <ThumbContainer>
              {isItemFolder ? (
                <ProjectOrFolderThumbContainer>
                  <IconFolderClosed
                    color={theme.colors.icon.primary}
                    size={24}
                  />
                </ProjectOrFolderThumbContainer>
              ) : (
                <MediaThumbnail
                  assetTypename={assetEntity?.__typename ?? ''}
                  contentBottomLeft={
                    Boolean(commentCount) && (
                      <CommentCountLabel
                        aria-label={TEXT.commentCountLabel({
                          count: commentCount || 0,
                        })}
                        count={commentCount || 0}
                      />
                    )
                  }
                  contentBottomRight={
                    (assetEntity?.__typename === 'VideoAsset' ||
                      assetEntity?.__typename === 'ModelAsset') && (
                      <TimecodeLabel>{assetDuration}</TimecodeLabel>
                    )
                  }
                  displayCategory={displayCategory}
                  height={assetHeight}
                  isImageFitPreferred={true}
                  media={
                    complete
                      ? getThumbMedia(assetEntity as QuickSearchAssetFragment)
                      : {}
                  }
                  width={assetWidth}
                />
              )}
            </ThumbContainer>
          </span>
        </Link>
      </ThumbnailLinkContainer>
      <DetailContainer>
        <Stack alignX="center" gap={8}>
          <TextWrapper>
            <Link href={href}>
              <FileNameAnchor
                data-testid="detailpane-filename-link"
                onClick={handleClick}
              >
                <Text
                  enableTooltip={true}
                  style={{
                    justifyContent: 'center',
                  }}
                  truncate={{
                    hasExtension: true,
                    type: 'middle',
                    padding: 0,
                  }}
                  variant="header"
                >
                  {asset?.name ?? ''}
                </Text>
                {asset?.__typename === 'VersionStackAsset' && (
                  <VersionCount count={asset?.versions?.length || 0} />
                )}
              </FileNameAnchor>
            </Link>
          </TextWrapper>
          <Inline
            alignX="center"
            alignY="center"
            gap={4}
            style={{ width: '100%' }}
          >
            {asset?.creator?.name && (
              <Text
                color="tertiary"
                data-testid="detailpane-creator-name"
                style={{
                  justifyContent: 'center',
                  maxWidth: `${CREATOR_NAME_MAX_WIDTH}px`,
                }}
                truncate={true}
                variant="secondary"
              >
                {asset?.creator?.name}
              </Text>
            )}
            {asset?.creator?.name && (
              <Text aria-hidden={true} color="tertiary" variant="secondary">
                ·
              </Text>
            )}
            <VisuallyHidden>
              {insertedAtDate ? TEXT.insertedDateTimeA11y(insertedAtDate) : ''}
            </VisuallyHidden>
            <Text
              aria-hidden={true}
              color="tertiary"
              textAlign="center"
              variant="secondary"
            >
              {insertedAtDate ? TEXT.insertedDateTime(insertedAtDate) : ''}
            </Text>
          </Inline>
        </Stack>
      </DetailContainer>
      <MetaContainer>
        <Stack
          alignX="center"
          gap={8}
          style={{
            width: '100%',
          }}
        >
          <MetadataRow
            data-testid="detailpane-filetype"
            icon={IconFiletypeFile}
            metadata={copy}
            title={isItemFolder ? TEXT.projectOrFolderType : TEXT.fileType}
          />
          {isItemFolder && (
            <MetadataRow
              data-testid="detailpane-itemcount"
              icon={IconClock}
              metadata={itemCount.toString()}
              metadataAriaText={`${itemCount} items`}
              title={TEXT.itemCount}
            />
          )}
          {(filesize || filesize === 0) && (
            <MetadataRow
              data-testid="detailpane-filesize"
              icon={IconCloud}
              metadata={formattedFilesize}
              title={TEXT.size}
            />
          )}
        </Stack>
      </MetaContainer>
    </DetailPane>
  );
};
